import * as React from 'react';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
import {StatusBadge} from 'shells/status-badge/status-badge';
import {Switch} from 'shells/switch/switch';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {Table} from 'shells/table/table';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {GetContextDocuments_contextDocuments as ContextDocument} from 'nutshell-graphql-types';

import {
    useToggleContextDocumentUsage,
    useRecrawlContextDocuments,
    useGetContextDocumentUsage,
} from '../../../graphql/hooks';

import {getStatusBadge} from '../../helpers';

import {ContextDocumentRowActionMenu} from '../context-document-row-action-menu';

import {getPrimaryText, getIcon} from './helpers';

type PrimaryColumnConfig = {
    id: string;
    header: string;
};

type Props = {
    documents: Array<ContextDocument | null | undefined>;
    title: string;
    firstRow?: React.ReactElement<any>;
    emptyState: React.ReactNode;
    primaryColumn: PrimaryColumnConfig;
    onRefetch: () => Promise<any>;
    isLoading: boolean;
    onSelectDocument: (document: ContextDocument) => void;
    canRecrawl?: boolean;
    canToggle?: boolean;
};

export const ContextDocumentTable = (props: Props) => {
    const {primaryColumn, canToggle} = props;
    const {toggleContextDocumentUsage} = useToggleContextDocumentUsage();
    const {recrawlContextDocuments} = useRecrawlContextDocuments();
    const {contextDocumentUsage} = useGetContextDocumentUsage();
    const wordsUsage = contextDocumentUsage ? contextDocumentUsage.wordsUsage : 0;

    const handleToggleContextDocumentUsage = React.useCallback(
        (document: ContextDocument) => {
            toggleContextDocumentUsage({
                documentId: document.id,
                isEnabled: !document.isIncluded,
                sourceType: document.sourceType,
            });
        },
        [toggleContextDocumentUsage]
    );

    const columns = React.useMemo(
        () => [
            {
                id: primaryColumn.id,
                Header: primaryColumn.header,
                accessor: (row: ContextDocument) => {
                    const primaryText = getPrimaryText({value: row});
                    const icon = getIcon({value: row});

                    return {
                        icon,
                        text: primaryText,
                        subtitle: row.summary,
                        subtitleDefault: 'No summary available',
                    };
                },
                dataType: 'primaryWithSubtitle',
                minWidth: 350,
            },
            {
                id: 'words',
                Header: 'Word count',
                accessor: (row: ContextDocument) => row.wordCount,
                dataType: 'integer',
                width: 75,
            },
            {
                id: 'lastCrawledTime',
                Header: 'Last crawled',
                accessor: (row: ContextDocument) => row.lastCrawledTime,
                dataType: 'timestamp',
                width: 100,
                maxWidth: 100,
                Cell: ({cell}: any) => <RelativeTimestamp datetime={cell.value} />,
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (row: ContextDocument) =>
                    getStatusBadge(row.effectiveStatus, row.sourceType),
                dataType: 'component',
                width: 90,
                Cell: ({cell}: any) => <StatusBadge {...cell.value} />,
            },
            {
                id: 'enabled',
                Header: 'Enabled',
                accessor: (row: ContextDocument) => row.isIncluded,
                dataType: 'component',
                width: 80,
                Cell: ({cell}: any) => {
                    return (
                        <Switch
                            isOn={cell.value}
                            isRounded={true}
                            isSmall={true}
                            onChange={() => handleToggleContextDocumentUsage(cell.row.original)}
                            shouldStopPropagation={true}
                            isDisabled={
                                (!canToggle && !cell.value) ||
                                cell.row.original.effectiveStatus === 'FAILED'
                            }
                        />
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [wordsUsage, canToggle]
    );

    return (
        <LegacySetupTableWrapper
            title={props.title}
            // @ts-expect-error
            isLoading={props.isLoading}
            hasHeaderBorder={true}
            hasHeaderCellBorders={true}
            actionItem={
                props.canRecrawl && (
                    <div className='mr-16'>
                        <Button
                            onClick={() => recrawlContextDocuments({documentId: undefined})}
                            icon={RetryRegularIcon}
                        >
                            Recrawl
                        </Button>
                    </div>
                )
            }
        >
            <LegacySetupTableContainer overflow='show' wrapsEntireTable={true}>
                <Table
                    onSelectRow={(row) => props.onSelectDocument(row.original)}
                    firstRow={props.firstRow}
                    data={props.documents}
                    // @ts-expect-error
                    columns={columns}
                    renderRowActionMenu={(row) => (
                        <ContextDocumentRowActionMenu
                            document={row.original}
                            onRefetch={props.onRefetch}
                            onSelectDocument={props.onSelectDocument}
                        />
                    )}
                    hasBorders={true}
                    hasHeaderCellBorders={true}
                    hasHeaderBorder={true}
                    fullWidth={true}
                    headerBackgroundColor='offWhite'
                    isStriped={true}
                    hasHoverState={true}
                    disableFilters={true}
                    specialStates={[
                        {
                            shouldRender: () => props.isLoading && props.documents.length === 0,
                            component: (
                                <div className='py-64 flex justify-center align-center'>
                                    <LoadingIcon fill={colors.orange} size={32} />
                                </div>
                            ),
                        },
                        {
                            shouldRender: () => !props.isLoading && props.documents.length === 0,
                            component: props.emptyState,
                        },
                    ]}
                />
            </LegacySetupTableContainer>
        </LegacySetupTableWrapper>
    );
};
